import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BoxShadowProps,
  PaddingProps,
  PositionProps,
  RightProps,
  SizeProps,
  TopProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  boxShadow,
  padding,
  position,
  right,
  size,
  space,
  themeGet,
  top,
  width,
  zIndex,
} from 'styled-system';

import { ButtonBase } from 'common/components/Button';
import Icon from 'common/components/Icon';
import { LinkRegularLR1 } from 'common/components/Typography';
import { SIDE_NAV_HEADER } from 'common/constants/z-index';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FillProps } from 'common/theme/system-types';
import type { ThemeHeightProps } from 'common/theme/system-utilities';
import { fill, themeHeight } from 'common/theme/system-utilities';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import type { DispatchState } from 'common/types/React';
import { useSideNavTracking } from './hooks/useSideNavTracking';
import { NavigationLine } from './NavigationListItem';
import SideNavHeaderTitle from './SideNavHeaderTitle';
import type { CurrentNavigation } from './types';
import { getNavigationData } from './utils';

const ARROW_ICON_WIDTH = 8;
const CLOSE_BUTTON_SIZE = 32;
const CLOSE_BUTTON_RIGHT = [16, null, 48, 64];

interface SideNavHeaderProps {
  fetchedNavigation: GraphqlNavigationItemProps;
  onClickCloseButton: () => void;
  currentNavigation: CurrentNavigation;
  setCurrentNavigation: (currentNavigation: CurrentNavigation) => void;
  selectedItem: Nullable<number>;
  setSelectedItem: DispatchState<Nullable<number>>;
  isCascading: boolean;
  logoUrl?: string;
  shopName?: string;
  shopUrl?: string;
  isLoading?: boolean;
  isTabsEnabled?: boolean;
}

interface BackButtonSectionProps {
  isRootLevel: boolean;
  backButtonProps: ReturnType<typeof useSideNavTracking>['backButtonProps'];
  isTabsEnabled: boolean;
}

const getParentNavigationData = (
  currentNavigation: CurrentNavigation,
  fetchedNavigation: GraphqlNavigationItemProps
) => {
  const pointer = [...currentNavigation];
  pointer.pop();

  return getNavigationData(pointer, fetchedNavigation);
};

const absoluteButtonStyles = {
  display: 'block',
  p: 'sp_0',
  position: 'absolute',
  top: 0,
};

const BackButton = styled(ButtonBase)<AlignItemsProps>`
  ${alignItems};
`;

const backButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  mx: ['sp_16', null, 'sp_24', 'sp_40'],
  my: ['sp_16', null, 'sp_32', 'sp_40'],
};

const CloseButton = styled(ButtonBase)<RightProps & PositionProps & SizeProps>`
  ${right};
  ${position};
  ${size};
`;

const closeButtonStyles: ComponentProps<typeof CloseButton> = {
  ...absoluteButtonStyles,
  p: 'sp_8',
  position: 'absolute',
  right: CLOSE_BUTTON_RIGHT,
  size: CLOSE_BUTTON_SIZE,
};

const BackArrow = styled(Icon)<WidthProps & FillProps & SizeProps & PaddingProps>`
  ${width};
  ${fill};
  ${space};
  ${padding};
`;

const backArrowStyles: ComponentProps<typeof BackArrow> = {
  name: 'arrowSmall',
  dataTestId: 'back-arrow',
  fill: 'sys.neutral.icon.default',
  pl: 'sp_0',
  pr: 'sp_16',
  width: ARROW_ICON_WIDTH,
};

const BackText = styled(LinkRegularLR1)`
  &:hover {
    color: ${themeGet('colors.sys.neutral.text.strong')};
    text-underline-offset: ${themeGet('space.sp_4')}px;
    text-decoration: underline;
    text-decoration-thickness: ${themeGet('space.sp_2')}px;
    text-decoration-color: ${themeGet('colors.sys.neutral.text.strong')};
  }
`;

const CloseIcon = styled(Icon)<SizeProps & FillProps & PaddingProps>`
  ${size};
  ${fill};
`;

const closeIconStyles: ComponentProps<typeof CloseIcon> = {
  name: 'closeSmall',
  dataTestId: 'close',
  fill: 'sys.neutral.icon.default',
  p: 'sp_16',
};

const HeaderContainer = styled(NavigationLine)<
  BackgroundColorProps & PositionProps & TopProps & BoxShadowProps & ZIndexProps & ThemeHeightProps
>`
  ${backgroundColor};
  ${position};
  ${top};
  ${boxShadow};
  ${zIndex};
  ${themeHeight};
`;

const headerContainerStyles: ComponentProps<typeof HeaderContainer> = {
  bg: 'sys.neutral.background.default',
  position: 'sticky',
  top: 0,
  justifyContent: 'flex-start',
  boxShadow: 'none',
  zIndex: SIDE_NAV_HEADER,
  mt: [null, null, 'sp_20', 'sp_24'],
  themeHeight: 'sp_64',
};

const useSideNavHeader = ({
  selectedItem,
  fetchedNavigation,
  currentNavigation,
  isCascading,
  setCurrentNavigation,
  setSelectedItem,
}: Omit<SideNavHeaderProps, 'onClickCloseButton'>) => {
  const hasSelectedItem = selectedItem !== null;
  const isRootLevel = !hasSelectedItem && (isCascading || currentNavigation.length === 0);

  const navigationParent = getParentNavigationData(currentNavigation, fetchedNavigation);

  const onBackButtonClick = useCallback(() => {
    const pointer = [...currentNavigation];
    pointer.pop();
    setCurrentNavigation(pointer);
    setSelectedItem(null);
  }, [currentNavigation]);

  return { isRootLevel, navigationParent, onBackButtonClick };
};

const BackButtonSection = ({
  isRootLevel,
  backButtonProps,
  isTabsEnabled,
}: BackButtonSectionProps) => {
  const { t } = useLocalization();
  if (isRootLevel) {
    return null;
  }

  const backTextKey = isTabsEnabled ? 'h24_go_back' : 'h24_all_categories';

  return (
    <BackButton {...backButtonProps} {...backButtonStyles}>
      <BackArrow {...backArrowStyles} />
      <BackText>{t(backTextKey)}</BackText>
    </BackButton>
  );
};

const SideNavHeader = ({
  onClickCloseButton,
  logoUrl,
  shopName,
  shopUrl,
  isLoading = false,
  isTabsEnabled = false,
  ...props
}: SideNavHeaderProps) => {
  const { isRootLevel, navigationParent, onBackButtonClick } = useSideNavHeader(props);

  const { backButtonProps, closeButtonProps } = useSideNavTracking({
    navigationParentName: navigationParent.name,
    onBackButtonClick,
    onClickCloseButton,
    shopName,
  });

  return (
    <HeaderContainer
      {...headerContainerStyles}
      {...(isRootLevel ? { px: [null, null, 'sp_32', 'sp_48'] } : {})}
    >
      <BackButtonSection
        isRootLevel={isRootLevel}
        backButtonProps={backButtonProps}
        isTabsEnabled={isTabsEnabled}
      />
      <SideNavHeaderTitle
        isRootLevel={isRootLevel}
        logoUrl={logoUrl}
        shopName={shopName}
        shopUrl={shopUrl}
        isLoading={isLoading}
        isTabsEnabled={isTabsEnabled}
      />
      <CloseButton {...closeButtonProps} {...closeButtonStyles}>
        <CloseIcon {...closeIconStyles} />
      </CloseButton>
    </HeaderContainer>
  );
};

export default SideNavHeader;
